import { SceneKey } from '@models/scene-keys';
import Phaser from 'phaser';
import { POPUP_DEPTH, DPR, MAX_WIDTH } from '@constants/common.const';
import Mustache from 'mustache';
import { GameConfigService } from '@services/core/GameConfigService';
import type { ModalSheetState, ModalSheetConfig } from './modal-sheet.types';
import { ModalSheetGeometry } from './ModalSheetGeometry';
import { ModalSheetScrollManager } from './ModalSheetScrollManager';
import { ModalSheetDragHandler } from './ModalSheetDragHandler';
import { ModalSheetRenderer } from './ModalSheetRenderer';
import { ModalSheetScrimManager } from './ModalSheetScrimManager';

export type { ModalSheetState, ModalSheetConfig } from './modal-sheet.types';

export abstract class ModalSheet<TemplateData>
  extends Phaser.GameObjects.Container
{
  protected readonly sceneRef: Phaser.Scene;
  protected cfg: Required<ModalSheetConfig<TemplateData>>;
  public state: ModalSheetState = 'hidden';

  // UI Elements
  protected scrim!: Phaser.GameObjects.Rectangle;
  protected scrimManager!: ModalSheetScrimManager<TemplateData>;
  protected sheetBg!: Phaser.GameObjects.RenderTexture;
  protected grabber!: Phaser.GameObjects.RenderTexture;
  protected drawerIcon!: Phaser.GameObjects.Container;
  protected contentViewport!: Phaser.GameObjects.Container;
  protected contentInner!: Phaser.GameObjects.Container;
  protected contentMaskGfx!: Phaser.GameObjects.Graphics;
  protected domElements: Phaser.GameObjects.DOMElement[] = [];
  private domCacheKeys: string[] = [];

  // Helpers
  private geometry!: ModalSheetGeometry<TemplateData>;
  private scrollManager!: ModalSheetScrollManager<TemplateData>;
  private dragHandler!: ModalSheetDragHandler<TemplateData>;
  private renderer!: ModalSheetRenderer<TemplateData>;
  private resizeHandler!: () => void;

  // Event listeners for cleanup
  private wheelHandler!: (
    p: Phaser.Input.Pointer,
    dx: number,
    dy: number,
  ) => void;
  private scrollZone?: Phaser.GameObjects.Zone;

  constructor(
    public scene: Phaser.Scene,
    public config: ModalSheetConfig<TemplateData> = {},
  ) {
    super(scene, 0, 0);
    this.sceneRef = scene;

    this.cfg = this.createConfig(config);
    scene.add.existing(this);

    this.setupScrim();
    this.setupVisuals();
    this.setupHelpers();

    // Initialize geometry before interactions
    this.relayout();

    this.setupInteractions();
    this.setupResizeHandler();
    this.createContent(this.contentInner);
    this.scrollManager.setContentHeight(
      this.geometry.measureContentHeight(this.contentInner),
    );
    this.scrollManager.reset();
    this.scrollManager.updateMask(this.state);

    // Start hidden
    this.setPosition(this.geometry.getXAligned(), this.geometry.hiddenY);
    this.setVisible(false);
  }

  protected abstract createContent(content: Phaser.GameObjects.Container): void;

  private createConfig(
    config: ModalSheetConfig<TemplateData>,
  ): Required<ModalSheetConfig<TemplateData>> {
    const w = Math.min(this.scene.scale.width, MAX_WIDTH * DPR);
    const h = this.scene.scale.height;
    return {
      width: config.width ?? w,
      collapsedHeight: config.collapsedHeight ?? Math.floor(h * 0.75),
      expandedHeight: config.expandedHeight ?? h,
      backgroundColor:
        config.backgroundColor ??
        new GameConfigService().getColor('--color-base-1'),
      backgroundAlpha: config.backgroundAlpha ?? 1,
      scrimColor: config.scrimColor ?? '#000C35',
      scrimAlpha: config.scrimAlpha ?? 0.7,
      cornerRadius: config.cornerRadius ?? 24 * DPR,
      grabberWidth: config.grabberWidth ?? 22 * DPR,
      grabberHeight: config.grabberHeight ?? 4 * DPR,
      grabberRadius: config.grabberRadius ?? 12 * DPR,
      grabberColor:
        config.grabberColor ??
        new GameConfigService().getColor('--color-neutral-100'),
      grabberAlpha: config.grabberAlpha ?? 0.25,
      animationMs: config.animationMs ?? 250,
      dismissOnScrim: config.dismissOnScrim ?? true,
      iconKey: config.iconKey ?? '',
      templateData: config.templateData ?? ({} as TemplateData),
      dataSupplier: config.dataSupplier,
    } as Required<ModalSheetConfig<TemplateData>>;
  }

  private setupScrim(): void {
    this.scrimManager = new ModalSheetScrimManager(
      this.scene,
      this.cfg,
      this.cfg.dismissOnScrim ? () => this.hide() : undefined,
    );
    this.scrim = this.scrimManager.scrim;
  }

  private setupVisuals(): void {
    this.setDepth(POPUP_DEPTH);

    this.sheetBg = this.scene.add.renderTexture(0, 0, 1, 1);
    this.sheetBg.setOrigin(0, 0);
    this.add(this.sheetBg);

    this.grabber = this.scene.add.renderTexture(0, 0, 1, 1);
    this.grabber.setOrigin(0, 0);
    this.add(this.grabber);

    this.contentMaskGfx = this.scene.add.graphics();
    this.add(this.contentMaskGfx);
    this.contentMaskGfx.setVisible(false);

    this.contentViewport = this.scene.add.container(0, 0);
    this.add(this.contentViewport);

    this.contentInner = this.scene.add.container(0, 0);
    this.contentInner.setVisible(false);
    this.contentViewport.add(this.contentInner);

    const mask = this.contentMaskGfx.createGeometryMask();
    this.contentViewport.setMask(mask);

    // Add drawerIcon after contentViewport so it renders on top
    this.drawerIcon = this.scene.add.container();
    this.add(this.drawerIcon);
  }

  private setupHelpers(): void {
    this.geometry = new ModalSheetGeometry(this.scene, this.cfg);
    this.scrollManager = new ModalSheetScrollManager(
      this.scene,
      this.geometry,
      this.contentInner,
      this.contentMaskGfx,
    );
    this.dragHandler = new ModalSheetDragHandler(
      this.scene,
      this.geometry,
      this,
      (target) => this.handleSnapComplete(target),
      () => this.updateScrimForY(),
    );
    this.renderer = new ModalSheetRenderer(
      this.scene,
      this.geometry,
      this.cfg,
      this.sheetBg,
      this.grabber,
      this.drawerIcon,
    );
  }

  private setupInteractions(): void {
    const dragZone = this.dragHandler.setupDragZone();
    this.add(dragZone);

    // Wheel scrolling - store handler for cleanup
    this.wheelHandler = (_p: Phaser.Input.Pointer, _dx: number, dy: number) => {
      if (this.state !== 'expanded') return;
      if (!this.scrollManager.isPointerOverViewport(this.x, this.y)) return;
      this.scrollManager.scroll(dy);
    };
    this.scene.input.on('wheel', this.wheelHandler);

    // Touch scrolling
    const dims = this.scrollManager.getViewportDimensions();
    this.scrollZone = this.scene.add
      .zone(
        this.geometry.padding.left,
        this.geometry.padding.top,
        dims.width,
        dims.height,
      )
      .setOrigin(0, 0)
      .setInteractive();

    this.add(this.scrollZone);

    let lastY = 0;
    this.scrollZone.on('pointerdown', (p: Phaser.Input.Pointer) => {
      lastY = p.y;
    });

    this.scrollZone.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!p.isDown || this.state !== 'expanded') return;
      const dy = p.y - lastY;
      lastY = p.y;
      this.scrollManager.scroll(-dy);
    });
  }

  private setupResizeHandler(): void {
    this.resizeHandler = () => {
      this.relayout();
      this.setY(this.geometry.getYForState(this.state));
      this.scrimManager.updateSize(
        this.scene.scale.width,
        this.scene.scale.height,
      );
    };
    this.scene.scale.on('resize', this.resizeHandler, this);
  }

  public showCollapsed(): void {
    this.show('collapsed');
  }

  public hide(): void {
    if (this.state === 'hidden') return;

    this.state = 'hidden';
    this.drawerIcon.setAlpha(0);
    this.contentInner.setVisible(false);
    this.setVisible(true);
    this.scrim.setVisible(true);

    // Immediately remove blurred background so user sees the game scene beneath
    this.scrimManager.hideBlur();

    this.sceneRef.tweens.add({
      targets: this,
      y: this.geometry.hiddenY,
      duration: this.cfg.animationMs,
      ease: 'Cubic.Out',
      onUpdate: () => this.updateScrimForY(),
      onComplete: () => {
        this.setVisible(false);
        this.scrimManager.hide();
      },
    });
  }

  private refreshDomContent(): void {
    // if a dataSupplier is set, fetches the raw (un-rendered) template
    // from Phaser's cache again and runs Mustache on it with new data.
    const supplier = this.cfg.dataSupplier as (() => TemplateData) | undefined;
    if (!supplier) return;
    const freshData = supplier();
    for (let i = 0; i < this.domElements.length; i++) {
      const raw = this.scene.cache.html.get(this.domCacheKeys[i]);
      if (!raw) continue;
      (this.domElements[i].node as HTMLElement).innerHTML = Mustache.render(
        raw,
        freshData,
      );
    }
  }

  protected show(target: 'collapsed' | 'expanded'): void {
    this.refreshDomContent();
    this.state = target;
    this.renderer.redraw(this.state);
    this.scrollManager.updateMask(this.state);
    this.updateDomContentHeight();

    const gameScene = this.sceneRef.scene.get(SceneKey.Game) as
      | Phaser.Scene
      | undefined;
    if (gameScene) {
      this.scrimManager.captureAndBlurScene(gameScene);
    }
    this.scrimManager.show();

    this.setVisible(true);
    this.scrim.setVisible(true);
    this.contentInner.setVisible(true);
    this.drawerIcon.setAlpha(target === 'expanded' ? 0 : 1);

    if (this.scrim.alpha === 0) {
      this.scrim.setAlpha(0);
    }

    const toY = this.geometry.getYForState(target);

    this.sceneRef.tweens.add({
      targets: this,
      y: toY,
      duration: this.cfg.animationMs,
      ease: 'Cubic.Out',
      onUpdate: () => this.updateScrimForY(),
      onStart: () => {
        if (this.y > this.geometry.hiddenY - 1) {
          this.y = this.geometry.hiddenY;
        }
      },
    });

    this.sceneRef.tweens.add({
      targets: this.scrim,
      alpha: 1,
      duration: this.cfg.animationMs,
      ease: 'Cubic.Out',
    });
  }

  protected relayout(): void {
    this.geometry.recalculate();
    this.renderer.redraw(this.state);
    this.contentViewport.setPosition(
      this.geometry.padding.left,
      this.geometry.padding.top,
    );
    this.scrollManager.setContentHeight(
      this.geometry.measureContentHeight(this.contentInner),
    );
    this.scrollManager.updateMask(this.state);
    this.setX(this.geometry.getXAligned());
  }

  protected createDomContent(
    cacheKey: string,
    data?: TemplateData,
  ): Phaser.GameObjects.DOMElement {
    const element = this.scene.add.dom(0, 0).createFromCache(cacheKey);

    if (data) {
      const html = (element.node as HTMLElement).innerHTML;
      (element.node as HTMLElement).innerHTML = Mustache.render(html, data);
    }
    element.setOrigin(0, 0).setScale(DPR);

    const { width, height } = this.scrollManager.getViewportDimensions();
    const domStyle = element.node as HTMLElement;
    domStyle.style.resize = 'none';
    domStyle.style.maxWidth = `${width / DPR}px`;
    domStyle.style.width = '100%';
    // these styles are necessary for scrolling
    domStyle.style.maxHeight = `${height / DPR}px`;
    domStyle.style.overflowY = 'auto';
    domStyle.style.pointerEvents = 'auto';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (domStyle.style as any).webkitOverflowScrolling = 'touch';

    // Track DOM element and its source cache key for later re-renders
    this.domElements.push(element);
    this.domCacheKeys.push(cacheKey);

    return element;
  }

  private updateScrimForY(): void {
    const t = Phaser.Math.Clamp(
      (this.geometry.hiddenY - this.y) /
        (this.geometry.hiddenY - this.geometry.expandedY),
      0,
      1,
    );
    this.scrim.setAlpha(t);
  }

  private updateDomContentHeight(): void {
    const { height } = this.scrollManager.getViewportDimensions();
    for (const domElement of this.domElements) {
      const domStyle = domElement.node as HTMLElement;
      domStyle.style.maxHeight = `${height / DPR}px`;
    }
  }

  private handleSnapComplete(target: ModalSheetState): void {
    if (target === 'hidden') {
      this.hide();
      return;
    }

    this.state = target;
    this.renderer.redraw(this.state);
    this.scrollManager.updateMask(this.state);
    this.updateDomContentHeight();

    const toY = this.geometry.getYForState(target);

    this.sceneRef.tweens.add({
      targets: this,
      y: toY,
      duration: this.cfg.animationMs,
      ease: 'Cubic.Out',
      onUpdate: () => this.updateScrimForY(),
    });
  }

  destroy(fromScene?: boolean): void {
    // Clean up resize listener
    if (this.resizeHandler) {
      this.scene.scale.off('resize', this.resizeHandler, this);
    }

    // Clean up wheel listener
    if (this.wheelHandler) {
      this.scene.input.off('wheel', this.wheelHandler);
    }

    // Clean up scroll zone listeners
    if (this.scrollZone) {
      this.scrollZone.off('pointerdown');
      this.scrollZone.off('pointermove');
    }

    // Clean up drag handler
    if (this.dragHandler) {
      this.dragHandler.destroy();
    }

    // Clean up scrim
    if (this.scrimManager) {
      this.scrimManager.destroy();
    }

    // Call parent destroy
    super.destroy(fromScene);
  }
}
